// src/components/promotions/PromotionFormModal.js
import React, { useState, useEffect } from 'react';
import { XMarkIcon, TagIcon, CalendarDaysIcon, UserGroupIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const PromotionFormModal = ({ isOpen, onClose, promotion, onSave, availableTypes, availableTargets }) => {
    const isEditing = !!promotion;
    const [formData, setFormData] = useState({
        name: '',
        type: availableTypes[0] || '',
        target: availableTargets[0] || '',
        startDate: '',
        endDate: '',
        details: '',
        // Add specific fields for different types, e.g., discount value, bonus points
    });

    useEffect(() => {
        if (promotion) {
            // If editing, pre-fill form with promotion data
            setFormData({
                name: promotion.name,
                type: promotion.type,
                target: promotion.target,
                startDate: promotion.startDate,
                endDate: promotion.endDate === 'Ongoing' ? '' : promotion.endDate, // Handle ongoing for input
                details: promotion.details,
            });
        } else {
            // If creating, reset form to default empty values
            setFormData({
                name: '',
                type: availableTypes[0] || '',
                target: availableTargets[0] || '',
                startDate: '',
                endDate: '',
                details: '',
            });
        }
    }, [promotion, isOpen, availableTypes, availableTargets]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Simple validation
        if (!formData.name || !formData.startDate || !formData.type || !formData.target) {
            alert('Please fill in all required fields: Name, Type, Target, and Start Date.');
            return;
        }

        // Prepare data for saving
        const dataToSave = {
            ...formData,
            id: promotion ? promotion.id : undefined, // Keep ID if editing
            endDate: formData.endDate || 'Ongoing' // Set to 'Ongoing' if empty
        };
        onSave(dataToSave);
    };

    const modalTitle = isEditing ? 'Edit Promotion' : 'Create New Promotion';
    const submitButtonText = isEditing ? 'Save Changes' : 'Create Promotion';

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">{modalTitle}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                {/* Modal Body */}
                <form onSubmit={handleSubmit} className="p-6">
                    <div className="grid grid-cols-1 gap-y-4">
                        {/* Promotion Name */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Promotion Name <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="name"
                                id="name"
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="e.g., Double Points Weekend"
                                required
                            />
                        </div>

                        {/* Promotion Type */}
                        <div>
                            <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">Promotion Type <span className="text-red-500">*</span></label>
                            <select
                                name="type"
                                id="type"
                                value={formData.type}
                                onChange={handleChange}
                                className="w-full border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                required
                            >
                                {availableTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                            <p className="mt-2 text-xs text-gray-500 flex items-center">
                                <InformationCircleIcon className="h-4 w-4 mr-1" />
                                Defines how the promotion works (e.g., gives points, applies discount).
                            </p>
                        </div>

                        {/* Target Audience */}
                        <div>
                            <label htmlFor="target" className="block text-sm font-medium text-gray-700 mb-1">Target Audience <span className="text-red-500">*</span></label>
                            <select
                                name="target"
                                id="target"
                                value={formData.target}
                                onChange={handleChange}
                                className="w-full border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                required
                            >
                                {availableTargets.map(target => (
                                    <option key={target} value={target}>{target}</option>
                                ))}
                            </select>
                            <p className="mt-2 text-xs text-gray-500 flex items-center">
                                <InformationCircleIcon className="h-4 w-4 mr-1" />
                                Who is eligible for this promotion.
                            </p>
                        </div>

                        {/* Date Range */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">Start Date <span className="text-red-500">*</span></label>
                                <input
                                    type="date"
                                    name="startDate"
                                    id="startDate"
                                    value={formData.startDate}
                                    onChange={handleChange}
                                    className="w-full border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">End Date (Optional)</label>
                                <input
                                    type="date"
                                    name="endDate"
                                    id="endDate"
                                    value={formData.endDate}
                                    onChange={handleChange}
                                    className="w-full border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                />
                                <p className="mt-2 text-xs text-gray-500">Leave empty for ongoing promotion.</p>
                            </div>
                        </div>

                        {/* Promotion Details/Description */}
                        <div>
                            <label htmlFor="details" className="block text-sm font-medium text-gray-700 mb-1">Details/Description</label>
                            <textarea
                                name="details"
                                id="details"
                                rows="3"
                                value={formData.details}
                                onChange={handleChange}
                                className="w-full border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                placeholder="Provide a brief description or specific terms (e.g., 'Earn 2x points on all purchases')."
                            ></textarea>
                        </div>

                        {/* Add more fields here based on promotion type later (e.g., Discount Amount, Bonus Points, Code) */}

                    </div>

                    {/* Modal Actions */}
                    <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors shadow-md"
                        >
                            {submitButtonText}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PromotionFormModal;